import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type CreateStopperRulePayload,
  type CreateStopperRuleResponse,
} from './types';
import { zodCreateStopperRuleSchema } from './validations';

/**
 * Creates a stopper rule for an invoice process.
 * @param createStopperRulePayload - The payload data for creating the stopper rule.
 * @returns A promise that resolves when the stopper rule is created.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/create_invoice_process_stopper_rule_vadmin_invoice_stopper_rule_post
 */
export const createInvoiceProcessStopperRule = async (
  createStopperRulePayload: CreateStopperRulePayload,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.INVOICE_REPROCESSING.CREATE_STOPPER_RULE(), {
        json: createStopperRulePayload,
        validate: { body: zodCreateStopperRuleSchema },
      })
      .json<CreateStopperRuleResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error creating invoice process stopper rule',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.createStopperRule.error404, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.createStopperRule.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.createStopperRule.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
